// ============================================================
// FinishedStockTab.jsx  —  Noble Enterprises (tape module)
//
// WHAT'S ON THIS SCREEN
// Finished tape in stock, keyed by pure spec: type, micron,
// finished width, finished length. A 69mm × 70yd roll is a
// 69mm × 70yd roll — there is no product catalogue above it and
// no customer lock beneath it.
//
// WHY NO CATALOGUE
// Every catalogue entry was a second place the truth could
// live. Cutting decides what exists; naming it afterwards only
// created rows that production had never made and products
// whose size disagreed with the stock filed under them.
//
// PRICE IS THE ONE EDITABLE FIELD
// Cost comes up from production and nobody touches it. Price is
// a commercial decision, it has nowhere else to live now the
// catalogue is gone, and Sales needs it to invoice against. So
// it is edited inline here, owner only — the server enforces
// that, this screen only hides the affordance.
//
// COST VS PRICE
// Both are shown deliberately. Cost is what the film and the
// cutting actually charged this stock; price is what it sells
// for. Seeing them side by side is how a spec priced under its
// own cost gets noticed before an invoice goes out.
//
// HOW THIS FILE IS ORGANISED
//   A. Shared UI bits
//   B. FinishedStockTab
// ============================================================

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Search, Check, X } from 'lucide-react';
import { hasFullAccess } from '../../../utils/access';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const C = {
  brand:      '#f39c12',
  text:       '#1a2332',
  textSoft:   '#64748b',
  textMuted:  '#94a3b8',
  border:     '#e2e8f0',
  borderSoft: '#f1f5f9',
  card:       '#ffffff',
  rowAlt:     '#fafbfc',
  danger:     '#dc2626',
  dangerBg:   '#fee2e2',
  success:    '#059669',
  successBg:  '#d1fae5',
  warning:    '#d97706',
  warningBg:  '#fef3c7',
  info:       '#0284c7',
  infoBg:     '#e0f2fe',
};

const num   = (n) => Number(n || 0);
const money = (n) => `Rs ${num(n).toLocaleString('en-PK', { maximumFractionDigits: 0 })}`;
const money2 = (n) => `Rs ${num(n).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Postgres numerics arrive as strings with trailing zeros
const mm = (n) => `${num(n).toLocaleString('en-PK', { maximumFractionDigits: 2 })} mm`;
const yd = (n) => `${num(n).toLocaleString('en-PK', { maximumFractionDigits: 2 })} yd`;


// ============================================================
// A. SHARED UI BITS
// ============================================================

const inputStyle = {
  width: '100%', padding: '10px 13px',
  border: `1px solid ${C.border}`, borderRadius: '8px',
  fontSize: '14px', color: C.text, boxSizing: 'border-box',
  fontFamily: 'inherit', background: '#ffffff',
};

const Banner = ({ type, message, onClose }) => {
  if (!message) return null;
  const isError = type === 'error';
  return (
    <div style={{
      background: isError ? C.dangerBg : C.successBg,
      color:      isError ? C.danger   : C.success,
      padding: '11px 16px', borderRadius: '8px', marginBottom: '16px',
      fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px',
    }}>
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', display: 'flex' }}>
        <X size={16} />
      </button>
    </div>
  );
};

const thStyle = {
  textAlign: 'left', padding: '11px 18px', fontSize: '12px',
  fontWeight: '600', color: C.textSoft, background: C.rowAlt,
  textTransform: 'uppercase', letterSpacing: '0.3px',
};
const tdStyle = {
  padding: '13px 18px', fontSize: '14px', color: C.text,
  borderTop: `1px solid ${C.borderSoft}`,
};

const StatCard = ({ label, value, colour = C.text, sub }) => (
  <div style={{
    background: C.card, border: `1px solid ${C.border}`,
    borderRadius: '10px', padding: '16px 18px',
  }}>
    <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '600', textTransform: 'uppercase' }}>
      {label}
    </div>
    <div style={{ fontSize: '21px', fontWeight: '700', color: colour, marginTop: '4px' }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: '11px', color: C.textMuted, marginTop: '2px' }}>{sub}</div>}
  </div>
);


// ============================================================
// B. MAIN COMPONENT
// ============================================================

const FinishedStockTab = () => {
  const [stock, setStock]     = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [banner, setBanner]   = useState({ type: '', message: '' });

  // Factory is what production delivers into; the shop is what
  // sells. Stock defaults to the factory because that is where
  // a cut run lands and where the next decision — what to send
  // over — gets made.
  const [location, setLocation] = useState('factory');

  // { id, value } — one row at a time
  const [editing, setEditing] = useState(null);
  const [savingPrice, setSavingPrice] = useState(false);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // The server is the authority on who may price. This only
  // decides whether to show the affordance — a 403 still comes
  // back if someone gets clever.
  const role = (() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}').role;
    } catch {
      return null;
    }
  })();
  const canPrice = hasFullAccess('adhesive');

  const notify = (type, message) => {
    setBanner({ type, message });
    if (type === 'success') setTimeout(() => setBanner({ type: '', message: '' }), 3000);
  };

    const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/tape/cut-runs/stock?location=${location}`,
        { headers }
      );
      setStock(res.data.data.stock || []);
      setSummary(res.data.data.summary || null);
    } catch {
      notify('error', 'Could not load stock. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [location]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const savePrice = async (id, value) => {
    const price = Number(value);
    if (!(price >= 0) || Number.isNaN(price)) {
      return notify('error', 'Enter a valid price');
    }

    setSavingPrice(true);
    try {
      await axios.patch(
        `${API}/tape/cut-runs/stock/${id}/price`,
        { price },
        { headers }
      );
      setEditing(null);
      notify('success', 'Price updated');
      load();
    } catch (err) {
      notify('error', err.response?.data?.message || 'Could not update price');
    } finally {
      setSavingPrice(false);
    }
  };

  // Filtered on the loaded list — the number of distinct specs
  // stays small, and a round trip per keystroke would be worse.
  const term = search.trim().toLowerCase();
  const visible = term
    ? stock.filter(s =>
        (s.tape_type_name || '').toLowerCase().includes(term) ||
        String(num(s.width_mm)).includes(term) ||
        String(num(s.length_yards)).includes(term) ||
        String(num(s.micron_value)).includes(term))
    : stock;

  // What the shelf would fetch at the prices set. Specs with no
  // price contribute nothing rather than zero — the difference
  // is visible in the sub-label.
  const priced = stock.filter(s => num(s.price) > 0);
  const retailValue = priced.reduce(
    (sum, s) => sum + (Number(s.quantity) * num(s.price)), 0
  );
  const unpriced = stock.length - priced.length;

  return (
    <div>
      <Banner
        type={banner.type}
        message={banner.message}
        onClose={() => setBanner({ type: '', message: '' })}
      />

            {/* Two shelves, one screen. A separate tab per location
          would duplicate everything below for no gain. */}
      <div style={{
        display: 'inline-flex', gap: '2px', padding: '3px',
        background: C.rowAlt, border: `1px solid ${C.border}`,
        borderRadius: '9px', marginBottom: '16px',
      }}>
        {[['factory', 'Factory'], ['shop', 'Shop']].map(([key, label]) => {
          const active = location === key;
          return (
            <button
              key={key}
              onClick={() => { setEditing(null); setLocation(key); }}
              style={{
                padding: '7px 18px', borderRadius: '7px', fontSize: '14px',
                fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
                border: 'none',
                background: active ? C.card : 'transparent',
                color:      active ? C.text : C.textSoft,
                boxShadow:  active ? '0 1px 2px rgba(0,0,0,0.06)' : 'none',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '14px', marginBottom: '20px',
      }}>
        <StatCard
          label="Pieces in stock"
          value={summary?.total_pieces ?? '—'}
          colour={C.brand}
        />
        <StatCard
          label="Different sizes"
          value={summary?.spec_count ?? '—'}
        />
                {canPrice && (
          <StatCard
            label="Stock at cost"
            value={summary ? money(summary.stock_value) : '—'}
            sub="what production charged"
          />
        )}
        <StatCard
          label="Stock at price"
          value={retailValue > 0 ? money(retailValue) : '—'}
          sub={
            unpriced > 0
              ? `${unpriced} size${unpriced === 1 ? '' : 's'} not priced yet`
              : 'all sizes priced'
          }
        />
      </div>

      <div style={{
        background: C.card, border: `1px solid ${C.border}`,
        borderRadius: '12px', overflow: 'hidden',
      }}>
        <div style={{
          padding: '18px 22px', borderBottom: `1px solid ${C.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: C.text }}>
              {location === 'shop' ? 'Shop stock' : 'Factory stock'}
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: '13px', color: C.textSoft }}>
              {location === 'shop'
                ? (canPrice
                    ? 'Click a price to set it. Sales invoices against these.'
                    : 'Transferred from the factory. Prices are set by the owner.')
                : 'Built by cutting runs. Transfer to the shop before it can be sold.'}
            </p>
          </div>

          <div style={{ position: 'relative', minWidth: '190px' }}>
            <Search
              size={15}
              style={{
                position: 'absolute', left: '12px', top: '50%',
                transform: 'translateY(-50%)', color: C.textMuted,
              }}
            />
            <input
              style={{ ...inputStyle, paddingLeft: '34px' }}
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search type or size…"
            />
          </div>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Tape</th>
                <th style={thStyle}>Size</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Pieces</th>
                                {canPrice && <th style={{ ...thStyle, textAlign: 'right' }}>Cost each</th>}
                <th style={{ ...thStyle, textAlign: 'right' }}>Price each</th>
                                {canPrice && <th style={{ ...thStyle, textAlign: 'right' }}>Margin</th>}
                <th style={{ ...thStyle, textAlign: 'right' }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={canPrice ? 7 : 5} style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '36px' }}>
                    Loading…
                  </td>
                </tr>
              )}

              {!loading && visible.map(row => {
                const qty   = Number(row.quantity);
                const cost  = num(row.cost_per_piece);
                const price = num(row.price);

                // A spec priced below what it cost to make is a
                // loss on every piece sold. Worth seeing on the
                // shelf, not in the P&L three weeks later.
                const margin    = price > 0 ? price - cost : null;
                const marginPct = price > 0 && cost > 0 ? (margin / price) * 100 : null;
                const underCost = margin !== null && margin < 0;

                const isEditing = editing?.id === row.id;

                return (
                  <tr key={row.id}>
                    <td style={{ ...tdStyle, fontWeight: '600' }}>
                      {row.tape_type_name}
                      <div style={{ fontSize: '11px', color: C.textMuted, fontWeight: '400' }}>
                        {num(row.micron_value)} micron
                      </div>
                    </td>
                    <td style={tdStyle}>
                      {mm(row.width_mm)} × {yd(row.length_yards)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '700', color: C.brand }}>
                      {qty}
                    </td>
                                        {canPrice && (
                      <td style={{ ...tdStyle, textAlign: 'right', color: C.textSoft }}>
                        {money2(cost)}
                      </td>
                    )}
                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      {isEditing ? (
                        <div style={{ display: 'inline-flex', gap: '4px', alignItems: 'center' }}>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            autoFocus
                            value={editing.value}
                            onChange={e => setEditing({ ...editing, value: e.target.value })}
                            onKeyDown={e => {
                              if (e.key === 'Enter')  savePrice(row.id, editing.value);
                              if (e.key === 'Escape') setEditing(null);
                            }}
                            style={{
                              width: '90px', padding: '5px 7px', fontSize: '13px',
                              border: `1px solid ${C.border}`, borderRadius: '5px',
                              textAlign: 'right', fontFamily: 'inherit',
                            }}
                          />
                          <button
                            onClick={() => savePrice(row.id, editing.value)}
                            disabled={savingPrice}
                            title="Save"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.success, display: 'flex' }}
                          >
                            <Check size={15} />
                          </button>
                          <button
                            onClick={() => setEditing(null)}
                            title="Cancel"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted, display: 'flex' }}
                          >
                            <X size={15} />
                          </button>
                        </div>
                      ) : canPrice ? (
                        <span
                          onClick={() => setEditing({ id: row.id, value: price > 0 ? String(price) : '' })}
                          title="Click to edit"
                          style={{
                            cursor: 'pointer',
                            borderBottom: `1px dashed ${C.border}`,
                            color: price > 0 ? C.text : C.textMuted,
                          }}
                        >
                          {price > 0 ? money2(price) : 'set price'}
                        </span>
                      ) : (
                        <span style={{ color: price > 0 ? C.text : C.textMuted }}>
                          {price > 0 ? money2(price) : '—'}
                        </span>
                      )}
                    </td>
                                        {canPrice && (
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {margin === null ? (
                          <span style={{ color: C.textMuted }}>—</span>
                        ) : (
                          <span style={{
                            fontWeight: '600', fontSize: '13px',
                            padding: '3px 9px', borderRadius: '10px',
                            background: underCost ? C.dangerBg : C.successBg,
                            color:      underCost ? C.danger   : C.success,
                          }}>
                            {marginPct !== null ? `${marginPct.toFixed(0)}%` : money2(margin)}
                          </span>
                        )}
                      </td>
                    )}
                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: '600' }}>
                      {price > 0 ? money(qty * price) : (
                        <span style={{ color: C.textMuted, fontWeight: '400' }}>
                                                    {canPrice ? money(qty * cost) : '—'}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}

              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan={canPrice ? 7 : 5} style={{ ...tdStyle, textAlign: 'center', color: C.textMuted, padding: '40px' }}>
                                       {term
                      ? 'Nothing matches that search.'
                      : location === 'shop'
                        ? 'Nothing at the shop yet. Transfer stock from the factory.'
                        : 'No finished stock yet. Record a cutting run to build stock.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {unpriced > 0 && !loading && (
        <p style={{ fontSize: '12px', color: C.textMuted, margin: '12px 2px 0', lineHeight: 1.6 }}>
          Sizes without a price show their cost in the value column instead. Sales
          cannot invoice a size until it has a price.
        </p>
      )}
    </div>
  );
};

export default FinishedStockTab;